/**
 * Unified Error Handler
 * 统一的错误处理和报告系统
 */

export enum ErrorLevel {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical'
}

export enum ErrorCategory {
  NETWORK = 'network',
  VALIDATION = 'validation',
  AUTHENTICATION = 'authentication',
  AUTHORIZATION = 'authorization',
  BUSINESS_LOGIC = 'business_logic',
  SYSTEM = 'system',
  UNKNOWN = 'unknown'
}

export interface ErrorContext {
  userId?: string;
  sessionId?: string;
  url?: string;
  userAgent?: string;
  timestamp: number;
  component?: string;
  action?: string;
  metadata?: Record<string, any>;
}

export interface AppError {
  id: string;
  message: string;
  stack?: string;
  level: ErrorLevel;
  category: ErrorCategory;
  context: ErrorContext;
  originalError?: Error;
  handled: boolean;
}

/**
 * Global Error Handler Class
 */
export class ErrorHandler {
  private static instance: ErrorHandler;
  private errorQueue: AppError[] = [];
  private maxQueueSize = 50;
  private isReporting = false;

  private constructor() {
    // Setup global error handlers
    this.setupGlobalHandlers();
  }

  static getInstance(): ErrorHandler {
    if (!ErrorHandler.instance) {
      ErrorHandler.instance = new ErrorHandler();
    }
    return ErrorHandler.instance;
  }

  /**
   * Setup global error handlers
   */
  private setupGlobalHandlers(): void {
    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.handleError(event.reason, {
        category: ErrorCategory.SYSTEM,
        level: ErrorLevel.HIGH,
        context: {
          component: 'global',
          action: 'unhandled_promise_rejection',
          timestamp: Date.now(),
          metadata: { reason: event.reason }
        }
      });
      event.preventDefault();
    });

    // Handle global errors
    window.addEventListener('error', (event) => {
      this.handleError(event.error, {
        category: ErrorCategory.SYSTEM,
        level: ErrorLevel.CRITICAL,
        context: {
          component: 'global',
          action: 'global_error',
          timestamp: Date.now(),
          metadata: {
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno,
            message: event.message
          }
        }
      });
    });
  }

  /**
   * Handle application errors
   */
  handleError(
    error: Error | string | unknown,
    options: {
      category?: ErrorCategory;
      level?: ErrorLevel;
      context?: Partial<ErrorContext>;
      silent?: boolean;
    } = {}
  ): AppError {
    const {
      category = ErrorCategory.UNKNOWN,
      level = ErrorLevel.MEDIUM,
      context = {},
      silent = false
    } = options;

    // Convert error to Error object if needed
    const errorObj = this.normalizeError(error);

    // Create error context
    const fullContext: ErrorContext = {
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: Date.now(),
      ...context
    };

    // Create AppError
    const appError: AppError = {
      id: this.generateErrorId(),
      message: errorObj.message,
      stack: errorObj.stack,
      level,
      category,
      context: fullContext,
      originalError: errorObj,
      handled: false
    };

    // Add to queue
    this.addToQueue(appError);

    // Log error (if not silent)
    if (!silent) {
      this.logError(appError);
    }

    // Report error (async)
    this.reportError(appError);

    return appError;
  }

  /**
   * Normalize error to Error object
   */
  private normalizeError(error: Error | string | unknown): Error {
    if (error instanceof Error) {
      return error;
    }

    if (typeof error === 'string') {
      return new Error(error);
    }

    // Handle other types
    try {
      return new Error(JSON.stringify(error));
    } catch {
      return new Error('Unknown error occurred');
    }
  }

  /**
   * Generate unique error ID
   */
  private generateErrorId(): string {
    return `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Add error to queue
   */
  private addToQueue(error: AppError): void {
    this.errorQueue.push(error);

    // Maintain queue size
    if (this.errorQueue.length > this.maxQueueSize) {
      this.errorQueue.shift();
    }
  }

  /**
   * Log error to console
   */
  private logError(error: AppError): void {
    const logMethod = this.getLogMethod(error.level);

    console.group(`🚨 ${error.level.toUpperCase()} Error [${error.category}]`);
    console[logMethod](error.message);
    if (error.stack) {
      console[logMethod](error.stack);
    }
    console.log('Context:', error.context);
    console.groupEnd();
  }

  /**
   * Get appropriate console method for error level
   */
  private getLogMethod(level: ErrorLevel): 'log' | 'warn' | 'error' {
    switch (level) {
      case ErrorLevel.CRITICAL:
      case ErrorLevel.HIGH:
        return 'error';
      case ErrorLevel.MEDIUM:
        return 'warn';
      default:
        return 'log';
    }
  }

  /**
   * Report error to external service
   */
  private async reportError(error: AppError): Promise<void> {
    if (this.isReporting) return;

    try {
      this.isReporting = true;

      // In development, just log
      if (import.meta.env.DEV) {
        console.log('📊 Error would be reported:', error.id);
        return;
      }

      // Report to error monitoring service (e.g., Sentry)
      // await this.sendToErrorService(error);

    } catch (reportError) {
      console.error('Failed to report error:', reportError);
    } finally {
      this.isReporting = false;
    }
  }

  /**
   * Get recent errors
   */
  getRecentErrors(limit = 10): AppError[] {
    return this.errorQueue.slice(-limit);
  }

  /**
   * Clear error queue
   */
  clearErrors(): void {
    this.errorQueue = [];
  }

  /**
   * Get error statistics
   */
  getErrorStats() {
    const stats = {
      total: this.errorQueue.length,
      byLevel: {} as Record<ErrorLevel, number>,
      byCategory: {} as Record<ErrorCategory, number>,
      recentErrors: this.getRecentErrors(5)
    };

    this.errorQueue.forEach(error => {
      stats.byLevel[error.level] = (stats.byLevel[error.level] || 0) + 1;
      stats.byCategory[error.category] = (stats.byCategory[error.category] || 0) + 1;
    });

    return stats;
  }
}

/**
 * Global error handler instance
 */
export const errorHandler = ErrorHandler.getInstance();

/**
 * Error boundary helper function
 */
export function handleAsyncError<T>(
  promise: Promise<T>,
  context?: Partial<ErrorContext>
): Promise<T> {
  return promise.catch(error => {
    errorHandler.handleError(error, {
      category: ErrorCategory.SYSTEM,
      level: ErrorLevel.MEDIUM,
      context: {
        action: 'async_operation',
        ...context
      }
    });
    throw error;
  });
}

/**
 * Create typed error
 */
export function createAppError(
  message: string,
  category: ErrorCategory = ErrorCategory.UNKNOWN,
  level: ErrorLevel = ErrorLevel.MEDIUM,
  context?: Partial<ErrorContext>
): Error {
  const error = new Error(message);

  // Add metadata
  (error as any).category = category;
  (error as any).level = level;
  (error as any).context = context;

  return error;
}

